import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { GOOGLE_REVIEWS_URL, TESTIMONIALS, type Testimonial } from "@/lib/testimonials";

/**
 * Reader quotes in one sideways row: a card at a time on a phone with the next
 * one peeking in, two across on a wider screen (the ad page is one narrow column). The row is one card tall
 * however many quotes there are. Scrolling is the browser's own scroll snap;
 * the dots and arrows only move it.
 *
 * Each card ends in a label in the style of Substack's paid badge: a plain
 * filled disc in the brand orange with the label beside it. Our own disc, not
 * Substack's artwork. The quotes carry no names, at the owner's request; each
 * label says where its quote came from, and the link points to the public
 * Google reviews.
 */
export function Testimonials({ className }: { className?: string }) {
  const trackRef = useRef<HTMLUListElement>(null);
  const [current, setCurrent] = useState(0);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const update = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const cards = Array.from(track.children) as HTMLElement[];
    let best = 0;
    let bestDistance = Infinity;
    cards.forEach((card, i) => {
      const distance = Math.abs(card.offsetLeft - track.offsetLeft - track.scrollLeft);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = i;
      }
    });
    setCurrent(best);
    setAtStart(track.scrollLeft < 4);
    setAtEnd(track.scrollLeft + track.clientWidth >= track.scrollWidth - 4);
  }, []);

  useEffect(update, [update]);

  function goTo(index: number) {
    const track = trackRef.current;
    const card = track?.children[Math.max(0, Math.min(index, TESTIMONIALS.length - 1))];
    if (!track || !(card instanceof HTMLElement)) return;
    track.scrollTo({ left: card.offsetLeft - track.offsetLeft, behavior: "smooth" });
  }

  return (
    <div className={className} data-testid="testimonials">
      <p className="mt-3 text-base text-foreground">
        Names withheld for privacy.
      </p>
      <ul
        ref={trackRef}
        onScroll={update}
        tabIndex={0}
        aria-label="Reader quotes, scroll sideways for more"
        className="mt-4 flex snap-x snap-mandatory gap-3 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {TESTIMONIALS.map((t) => (
          <TestimonialCard key={t.id} testimonial={t} />
        ))}
      </ul>
      <div className="mt-3 flex items-center justify-between gap-3">
        <div className="flex gap-1.5">
          {TESTIMONIALS.map((t, i) => (
            <button
              key={t.id}
              type="button"
              aria-label={`Quote ${i + 1}`}
              aria-current={i === current}
              onClick={() => goTo(i)}
              className={cn(
                "h-1.5 cursor-pointer rounded-full transition-all",
                i === current ? "w-5 bg-primary" : "w-1.5 bg-border"
              )}
            />
          ))}
        </div>
        <div className="flex shrink-0 gap-2">
          <ArrowButton label="Previous quote" disabled={atStart} onClick={() => goTo(current - 1)}>
            &larr;
          </ArrowButton>
          <ArrowButton label="Next quote" disabled={atEnd} onClick={() => goTo(current + 1)}>
            &rarr;
          </ArrowButton>
        </div>
      </div>
      <p className="mt-4 text-base">
        <a
          href={GOOGLE_REVIEWS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-primary underline underline-offset-4 hover:text-primary/80"
        >
          Read the Capitalist Times reviews on Google
        </a>
      </p>
    </div>
  );
}

function ArrowButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="h-9 w-9 cursor-pointer rounded-full border border-border bg-background text-foreground disabled:cursor-default disabled:opacity-35"
    >
      {children}
    </button>
  );
}

function TestimonialCard({ testimonial }: { testimonial: Testimonial }) {
  return (
    <li className="flex shrink-0 grow-0 basis-[86%] snap-start sm:basis-[calc((100%-0.75rem)/2)]">
      <figure className="flex w-full flex-col rounded-xl text-left border border-border bg-card p-4 sm:p-5">
        <span aria-hidden="true" className="h-6 font-heading text-5xl leading-none text-primary">
          &ldquo;
        </span>
        <blockquote className="mt-2 flex-1 text-base leading-relaxed text-foreground">
          {testimonial.quote}
        </blockquote>
        <figcaption className="mt-4 flex items-center gap-2 border-t border-border pt-3 text-base font-semibold text-foreground">
          <span aria-hidden="true" className="h-2.5 w-2.5 shrink-0 rounded-full bg-primary" />
          {testimonial.label}
        </figcaption>
      </figure>
    </li>
  );
}
